import { useCallback, useState } from "react";
import { useClassifySample } from "@/hooks/useClassifySample";
import type { SampleClassifyResponse } from "@/types";

/**
 * 2026-09-23: v1 시절 값(모델 버전·임계값 0.5·입력 ≤5·prob 0.47~0.52)에 멈춰 있던
 * 문구를 현재 운영 모델 기준으로 맞췄다. 근거
 * docs/2026/260922_인하대_RoMSH_v3_검토_재현.md §1-2·§5.
 * 값 출처: backend/inha_models/romsh_classifier.py(_MODEL_VERSION·체크포인트 threshold),
 * backend/app/services/analysis_workflow.py(_ROMSH_MAX_COMPONENTS=8, _ROMSH_ABUNDANCE_RESERVE=6).
 * 2026-09-30 PLAN 2-9d: 입력 문구를 기록 버전별로 나눴다. 같은 날 사용자 결정으로 운영은 v1(매칭
 * 점수), v2(TIC 신호 비율 작업가설)는 검토 중 — docs/2026/260930_RoMSH_TIC높이_구현검증.md.
 */
const ROMSH_MODEL_VERSION = "romsh-v2-rt8-2026-07-08";
/** 체크포인트에 들어 있는 모델 임계값 — 화면 표시는 소수 3자리 */
const ROMSH_THRESHOLD = 0.951;
/** 모델에 넣는 성분 수 상한 (analysis_workflow._ROMSH_MAX_COMPONENTS) */
const ROMSH_MAX_COMPONENTS = 8;

/** 모델 판정 → 색상 클래스 (우리가 정한 확률 구간을 쓰지 않는다) */
function labelColorClass(label: 0 | 1 | null): string {
  if (label === 1) return "text-red-700";
  if (label === 0) return "text-green-700";
  return "text-gray-600";
}

/** 모델 판정 라벨 (S23-E: 방화/자연발화 단정 제거) */
function directionLabel(label: 0 | 1 | null): string {
  if (label === 1) return "모델 판정: 인화성 패턴 유사";
  if (label === 0) return "모델 판정: 유사 아님";
  return "모델 판정 없음";
}

export default function RoMSHModelCard() {
  const [showTester, setShowTester] = useState(false);
  // 줄 단위 SMILES 입력 (최대 8줄 — 모델 상한)
  const [smilesText, setSmilesText] = useState("");
  // 줄 단위 RT 입력 (선택, 미입력 시 0.0)
  const [rtText, setRtText] = useState("");
  // 줄 단위 abundance 입력 (선택, 미입력 시 균등)
  const [abundanceText, setAbundanceText] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const classify = useClassifySample();

  const handleToggleTester = useCallback(() => {
    setShowTester((v) => !v);
    setValidationError(null);
    classify.reset();
  }, [classify]);

  const handleClassify = useCallback(() => {
    setValidationError(null);

    const smilesList = smilesText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    if (smilesList.length === 0) {
      setValidationError("SMILES를 최소 1개 입력해 주세요.");
      return;
    }
    if (smilesList.length > ROMSH_MAX_COMPONENTS) {
      setValidationError(
        `SMILES는 최대 ${ROMSH_MAX_COMPONENTS}개까지 입력 가능합니다.`,
      );
      return;
    }

    const rtList = rtText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const abList = abundanceText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    // RT 파싱 — 미입력 시 0.0
    const rts: number[] =
      rtList.length > 0
        ? rtList.map((v) => Number(v))
        : smilesList.map(() => 0.0);

    // abundance 파싱 — 미입력 시 균등
    const equal = 1.0 / smilesList.length;
    const abs: number[] =
      abList.length > 0
        ? abList.map((v) => Number(v))
        : smilesList.map(() => equal);

    if (rts.length !== smilesList.length) {
      setValidationError(
        `RT 입력 수(${rts.length})가 SMILES 수(${smilesList.length})와 다릅니다.`,
      );
      return;
    }
    if (abs.length !== smilesList.length) {
      setValidationError(
        `Abundance 입력 수(${abs.length})가 SMILES 수(${smilesList.length})와 다릅니다.`,
      );
      return;
    }
    if (rts.some(Number.isNaN) || abs.some(Number.isNaN)) {
      setValidationError(
        "RT 또는 Abundance에 숫자가 아닌 값이 포함되어 있습니다.",
      );
      return;
    }

    classify.mutate({
      sample_id: null,
      mixture: smilesList.map((smiles, i) => ({
        smiles,
        retention_time: rts[i],
        abundance: abs[i],
      })),
    });
  }, [smilesText, rtText, abundanceText, classify]);

  const result: SampleClassifyResponse | undefined = classify.data;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* 카드 헤더 */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 bg-indigo-500 rounded-full" />
            <h3 className="text-lg font-semibold text-gray-900">
              RoMSH (인하대 트랜스포머)
            </h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              신규
            </span>
          </div>
          <p className="text-sm text-gray-600">
            Retention-time-aware Mixture Sequence Handler — 혼합물(시료) 수준
            인화성 물질 패턴 유사도 분류기 (보조 연구지표)
          </p>
        </div>
        <button
          type="button"
          onClick={handleToggleTester}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors whitespace-nowrap"
        >
          {showTester ? "닫기" : "라이브 테스트"}
        </button>
      </div>

      {/* 모델 메타 정보 */}
      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            모델 버전
          </span>
          <p className="font-mono text-gray-900 text-xs mt-0.5">
            {ROMSH_MODEL_VERSION}
          </p>
        </div>
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            기본 임계값
          </span>
          <p className="text-gray-900 mt-0.5">{ROMSH_THRESHOLD}</p>
        </div>
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            입력
          </span>
          <p className="text-gray-900 mt-0.5">
            SMILES 최대 8 + RT + 풍부도. 현재 운영(입력 기록 v1, 선정 규칙
            v2.1): 물질마다 대표 1건 → 매칭 상위 6 + RT 구간 보충 2, 풍부도
            자리에 매칭 점수(인하대 학습 정의 확인 전까지). 입력 기록 v2(선정
            규칙 v3.tic-height)는 검토 중이며 운영 미사용: 스캔마다 대표 후보
            1건, 풍부도 = TIC 신호 비율(운영자 승인 작업가설 — 학습 산출식
            미확인). 분석마다 저장된 입력 기록이 기준
          </p>
        </div>
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            출력
          </span>
          <p className="text-gray-900 mt-0.5">
            확률 0~1 + 모델 판정(확률 ≥ 모델 임계값이면 유사)
          </p>
        </div>
      </div>

      {/* 프리뷰 disclaimer */}
      <div className="mb-4 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
        보조 연구지표 — 방화 여부를 판단하는 모델이 아닙니다. 모델 판정은 이
        카드에 적힌 모델 임계값 기준이며, 분석마다 저장된 값이 기준입니다. 학습
        데이터 범위·확률 보정(temperature) 값은 인하대에서 받지 못했습니다.
      </div>

      {/* 라이브 테스터 (toggle) */}
      {showTester && (
        <div className="border-t border-gray-200 pt-4 mt-2">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">
            RoMSH 라이브 테스터
          </h4>

          <div className="space-y-3">
            {/* SMILES 입력 */}
            <div>
              <label
                htmlFor="romsh-smiles"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                SMILES 입력 (줄 단위, 최대 5개)
              </label>
              <textarea
                id="romsh-smiles"
                value={smilesText}
                onChange={(e) => setSmilesText(e.target.value)}
                placeholder="SMILES 입력 (줄 단위 — CCCCCCCC, c1ccccc1 등)"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>

            {/* RT 입력 */}
            <div>
              <label
                htmlFor="romsh-rt"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                RT (분, 선택 — 미입력 시 0.0)
              </label>
              <textarea
                id="romsh-rt"
                value={rtText}
                onChange={(e) => setRtText(e.target.value)}
                placeholder="예: 5.0"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>

            {/* Abundance 입력 */}
            <div>
              <label
                htmlFor="romsh-abundance"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                Abundance (선택 — 미입력 시 균등 분배)
              </label>
              <textarea
                id="romsh-abundance"
                value={abundanceText}
                onChange={(e) => setAbundanceText(e.target.value)}
                placeholder="예: 100.0"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>

            {/* 예시 버튼 */}
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  setSmilesText("CCCCCCCC\nc1ccccc1\nCc1ccccc1");
                  setRtText("5.0\n8.2\n11.3");
                  setAbundanceText("1.0\n0.6\n0.4");
                }}
                className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                휘발유 유사 예시
              </button>
              <button
                type="button"
                onClick={() => {
                  setSmilesText("CCO\nCCOCC\nCC(=O)O");
                  setRtText("1.5\n3.2\n4.8");
                  setAbundanceText("0.5\n0.3\n0.2");
                }}
                className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                이종원자 예시
              </button>
            </div>

            {/* 유효성 오류 */}
            {validationError && (
              <p className="text-xs text-red-600">{validationError}</p>
            )}

            {/* 분류 실행 버튼 */}
            <button
              type="button"
              onClick={handleClassify}
              disabled={classify.isPending || !smilesText.trim()}
              className="w-full py-2 px-4 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {classify.isPending ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  분류 중...
                </>
              ) : (
                "분류 실행"
              )}
            </button>

            {/* 오류 표시 */}
            {classify.isError && (
              <p className="text-xs text-red-600">
                RoMSH 서비스 연결에 실패했습니다. 백엔드 AI Mock 서버가 실행
                중인지 확인하세요.
              </p>
            )}

            {/* 결과 표시 */}
            {result && (
              <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
                <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  분류 결과
                </h5>

                {result.status !== "completed" ? (
                  <p className="text-xs text-gray-500">상태: {result.status}</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-sm font-bold ${labelColorClass(result.label)}`}
                      >
                        {directionLabel(result.label)}
                      </span>
                      <span className="text-xs font-mono text-gray-600">
                        prob:{" "}
                        {result.probability !== null
                          ? result.probability.toFixed(4)
                          : "—"}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <div>
                        <span className="font-medium">logit:</span>{" "}
                        {result.logit !== null ? result.logit.toFixed(4) : "—"}
                      </div>
                      <div>
                        <span className="font-medium">임계값:</span>{" "}
                        {result.threshold ?? "—"}
                      </div>
                      <div>
                        <span className="font-medium">모델:</span>{" "}
                        <span className="font-mono">
                          {result.model_version ?? "—"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">추론 시간:</span>{" "}
                        {result.inference_time_ms !== null
                          ? `${result.inference_time_ms}ms`
                          : "—"}
                      </div>
                    </div>

                    <p className="text-xs text-amber-600">
                      보조 연구지표 — 판정은 위에 적힌 모델 임계값 기준입니다.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
